import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SignaturePad } from "../../components/SignaturePad";
import { CameraButtons } from "../../components/CameraButtons";
import { BarcodeScan } from "../../components/BarcodeScan";
import { Plus, Trash2, Check, ArrowRight, ArrowLeft, UserPlus, ScanBarcode, Share2, Download } from "lucide-react";

const STEPS = ["Verkäufer", "Artikel", "Prüfen & Vertrag", "Unterschrift"];

const EMPTY_POS = { product_id: "", name: "", qty: 1, purchase_price: "", sale_price: "", condition: "gut", serial: "", category_id: "", category_name: "", photos: [] };

const loadDraft = () => {
  try {
    return JSON.parse(localStorage.getItem("ankauf_draft") || "null");
  } catch {
    return null;
  }
};

export default function AnkaufNeu() {
  const navigate = useNavigate();
  const [step, setStep] = useState(() => {
    const s = loadDraft()?.step;
    return typeof s === "number" && s >= 0 && s <= 2 ? s : 0;
  });
  const [sellers, setSellers] = useState([]);
  const [seller, setSeller] = useState(() => loadDraft()?.seller || null);
  const [sellerSearch, setSellerSearch] = useState("");
  const [newSellerOpen, setNewSellerOpen] = useState(false);
  const [newSeller, setNewSeller] = useState({ first_name: "", last_name: "", birth_date: "", address: "", zip: "", city: "", phone: "", email: "", id_type: "", id_number: "" });
  const [positions, setPositions] = useState(() => {
    const d = loadDraft();
    return d?.positions?.length ? d.positions : [{ ...EMPTY_POS }];
  });
  const [products, setProducts] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(() => loadDraft()?.paymentMethod || "bar");
  const [purchaseVatMode, setPurchaseVatMode] = useState(() => loadDraft()?.purchaseVatMode || "privat");
  const [purchase, setPurchase] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanFor, setScanFor] = useState(null);

  useEffect(() => {
    const d = loadDraft();
    if (d?.positions?.some((p) => p.name || p.purchase_price)) toast.info("Ankauf-Entwurf wiederhergestellt.");
  }, []);

  useEffect(() => {
    if (purchase) return;
    try {
      localStorage.setItem("ankauf_draft", JSON.stringify({ positions, seller, paymentMethod, purchaseVatMode, step }));
    } catch {}
  }, [positions, seller, paymentMethod, purchaseVatMode, step, purchase]);

  useEffect(() => {
    api.get("/sellers").then((r) => setSellers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const filteredSellers = sellers.filter((s) =>
    `${s.first_name} ${s.last_name} ${s.number} ${s.phone}`.toLowerCase().includes(sellerSearch.toLowerCase()));

  const createSeller = async () => {
    try {
      const { data } = await api.post("/sellers", newSeller);
      setSellers([data, ...sellers]);
      setSeller(data);
      setNewSellerOpen(false);
      toast.success(`Verkäufer ${data.number} angelegt und übernommen.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setPos = (i, k, v) => {
    const next = [...positions];
    next[i] = { ...next[i], [k]: v };
    if (k === "product_id" && v) {
      const p = products.find((x) => x.id === v);
      if (p) { next[i].name = p.name; next[i].category_id = p.category_id; next[i].category_name = p.category_name; }
    }
    setPositions(next);
  };

  const total = positions.reduce((s, p) => s + (parseFloat(p.purchase_price) || 0) * (parseInt(p.qty) || 1), 0);

  const createPurchase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/purchases", {
        seller_id: seller.id, payment_method: paymentMethod, purchase_vat_mode: purchaseVatMode,
        positions: positions.map((p) => ({
          product_id: p.product_id || undefined, name: p.name, qty: parseInt(p.qty) || 1,
          purchase_price: parseFloat(p.purchase_price) || 0, sale_price: parseFloat(p.sale_price) || 0,
          condition: p.condition, serial: p.serial, category_id: p.category_id || undefined, category_name: p.category_name,
          photos: (p.photos || []).map((ph, x) => ({ label: ph.label || `Foto ${x + 1}`, data: ph.data })),
        })),
      });
      setPurchase(data);
      setStep(3);
      toast.success(`Ankauf ${data.number} erstellt – ${data.article_ids.length} Artikel im Bestand.`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const sign = async (sig) => {
    try {
      await api.post(`/purchases/${purchase.id}/sign`, { signature: sig });
      toast.success("Vertrag unterschrieben. Ankauf abgeschlossen.");
      navigate("/app/ankauf");
    } catch (e) { toast.error(apiError(e)); }
  };

  const contractBlob = async () => {
    const { data } = await api.get(`/contracts/${purchase.contract.id}/pdf`, { responseType: "blob" });
    return new Blob([data], { type: "application/pdf" });
  };

  const downloadContract = async () => {
    const blob = await contractBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${purchase.contract.number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareContract = async () => {
    try {
      const blob = await contractBlob();
      const file = new File([blob], `${purchase.contract.number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Kaufvertrag ${purchase.contract.number}` });
      } else {
        await downloadContract();
        toast.info("Teilen wird von diesem Gerät nicht unterstützt – PDF wurde heruntergeladen.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Teilen fehlgeschlagen");
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="ankauf-neu-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Ankauf</div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Neuer Ankauf</h1>
      </div>

      <div className="flex gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex-1 text-center text-[11px] font-mono py-2 rounded-md border ${
            step > i ? "bg-zinc-900 text-white border-zinc-900" : step === i ? "border-zinc-900 text-zinc-900" : "border-zinc-200 text-zinc-400"}`}>
            {i + 1}. {s}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex gap-3">
            <Input placeholder="Verkäufer suchen (Name, Nummer, Telefon)…" value={sellerSearch}
              onChange={(e) => setSellerSearch(e.target.value)} data-testid="seller-search-input" />
            <Button variant="outline" onClick={() => setNewSellerOpen(true)} data-testid="new-seller-button">
              <UserPlus className="w-4 h-4 mr-1" /> Neuer Verkäufer
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-zinc-50 border border-zinc-100 rounded-lg">
            {filteredSellers.length === 0 && <div className="p-4 text-sm text-zinc-400">Keine Treffer – neuen Verkäufer anlegen.</div>}
            {filteredSellers.map((s) => (
              <button key={s.id} onClick={() => setSeller(s)} data-testid={`seller-option-${s.number}`}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-zinc-50 flex justify-between items-center ${seller?.id === s.id ? "bg-zinc-900 text-white hover:bg-zinc-900" : ""}`}>
                <span>{s.first_name} {s.last_name}</span>
                <span className={`font-mono text-xs ${seller?.id === s.id ? "text-zinc-300" : "text-zinc-400"}`}>{s.number}</span>
              </button>
            ))}
          </div>
          {seller && <div className="text-sm bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3" data-testid="seller-selected">Ausgewählt: <b>{seller.first_name} {seller.last_name}</b> · {seller.number} {seller.id_number && `· Ausweis ${seller.id_type} ${seller.id_number}`}</div>}
          <div className="flex justify-end">
            <Button disabled={!seller} onClick={() => setStep(1)} data-testid="ankauf-step1-next" className="bg-zinc-900 hover:bg-zinc-800 text-white">Weiter <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-4">
          {positions.map((p, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-4 space-y-3" data-testid={`position-${i}`}>
              <div className="flex justify-between items-center">
                <span className="font-mono text-xs text-zinc-400">Position {i + 1}</span>
                {positions.length > 1 && (
                  <button onClick={() => setPositions(positions.filter((_, x) => x !== i))} className="text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Vorhandenes Produkt</Label>
                  <select value={p.product_id} onChange={(e) => setPos(i, "product_id", e.target.value)} data-testid={`position-product-select-${i}`}
                    className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">— Neu / manuell —</option>
                    {products.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                  </select>
                </div>
                {!p.product_id && (
                  <div className="col-span-2"><Label>Produktname</Label><Input value={p.name} onChange={(e) => setPos(i, "name", e.target.value)} placeholder="z. B. Apple iPhone 15 Pro 256GB" data-testid={`position-name-${i}`} className="mt-1.5" /></div>
                )}
                <div><Label>Menge</Label><Input type="number" min="1" value={p.qty} onChange={(e) => setPos(i, "qty", e.target.value)} data-testid={`position-qty-${i}`} className="mt-1.5" /></div>
                <div><Label>Zustand</Label>
                  <select value={p.condition} onChange={(e) => setPos(i, "condition", e.target.value)} data-testid={`position-condition-${i}`} className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
                    {["neu", "wie neu", "sehr gut", "gut", "akzeptabel", "defekt"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><Label>Einkaufspreis / Stück (€)</Label><Input type="number" step="0.01" value={p.purchase_price} onChange={(e) => setPos(i, "purchase_price", e.target.value)} data-testid={`position-price-${i}`} className="mt-1.5" /></div>
                <div><Label>Verkaufspreis / Stück (€)</Label><Input type="number" step="0.01" value={p.sale_price} onChange={(e) => setPos(i, "sale_price", e.target.value)} data-testid={`position-saleprice-${i}`} className="mt-1.5" /></div>
                <div className="col-span-2">
                  <Label>Seriennr. / IMEI (bei Menge 1)</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input value={p.serial} onChange={(e) => setPos(i, "serial", e.target.value)} data-testid={`position-serial-${i}`} className="font-mono flex-1" />
                    <Button type="button" variant="outline" onClick={() => setScanFor(i)} data-testid={`position-scan-${i}`} className="min-h-[44px] px-3">
                      <ScanBarcode className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Fotos (Vorderseite, Rückseite, Display, Zubehör, Verpackung…)</Label>
                  <CameraButtons compact onCapture={(imgs) => setPos(i, "photos", [...(p.photos || []), ...imgs.map((d, x) => ({ label: `Foto ${(p.photos?.length || 0) + x + 1}`, data: d }))])} />
                  {(p.photos || []).length > 0 && (
                    <div className="flex gap-2 flex-wrap" data-testid={`position-photos-${i}`}>
                      {p.photos.map((ph, pi) => (
                        <div key={pi} className="relative">
                          <img src={ph.data} alt={ph.label} className="w-16 h-16 object-cover rounded-lg border border-zinc-200" />
                          <button type="button" onClick={() => setPos(i, "photos", p.photos.filter((_, x) => x !== pi))}
                            className="absolute -top-1.5 -right-1.5 bg-zinc-900 text-white rounded-full w-5 h-5 text-[10px] flex items-center justify-center" data-testid={`position-photo-remove-${i}-${pi}`}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={() => setPositions([...positions, { ...EMPTY_POS }])} data-testid="add-position-button"><Plus className="w-4 h-4 mr-1" /> Position hinzufügen</Button>
          <div className="flex justify-between items-center border-t border-zinc-100 pt-4">
            <span className="text-sm text-zinc-500">Gesamt Einkauf</span>
            <span className="font-display text-xl font-semibold" data-testid="ankauf-total">{eur(total)}</span>
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
            <Button onClick={() => setStep(2)} disabled={!positions.every((p) => (p.product_id || p.name) && parseFloat(p.purchase_price) >= 0 && p.purchase_price !== "")}
              data-testid="ankauf-step2-next" className="bg-zinc-900 hover:bg-zinc-800 text-white">Weiter <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="ankauf-review">
          <h3 className="font-display font-medium text-lg">Prüfen & Vertrag erstellen</h3>
          <div className="text-sm border border-zinc-100 rounded-lg p-4 space-y-1 bg-zinc-50/50">
            <div><span className="text-zinc-400">Verkäufer:</span> {seller.first_name} {seller.last_name} ({seller.number})</div>
            {positions.map((p, i) => (
              <div key={i}><span className="text-zinc-400">Position {i + 1}:</span> {p.qty}× {p.name} · {p.condition} · {eur(p.purchase_price)}/Stück</div>
            ))}
            <div className="pt-2 border-t border-zinc-200 font-medium">Gesamt: {eur(total)}</div>
          </div>
          <div>
            <Label>Zahlungsart</Label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="payment-method-select" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
              {["bar", "überweisung", "karte", "paypal"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <Label>Einkauf von</Label>
            <select value={purchaseVatMode} onChange={(e) => setPurchaseVatMode(e.target.value)} data-testid="purchase-vat-mode" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="privat">Privatperson (§25a – keine Vorsteuer)</option>
              <option value="lieferant">Lieferant (19 % MwSt – Vorsteuer abziehbar)</option>
            </select>
            {purchaseVatMode === "lieferant" && (
              <div className="text-xs text-zinc-500 mt-1.5" data-testid="purchase-vat-hint">
                Enthaltene Vorsteuer: <b className="font-mono">{eur(Math.round((total - total / 1.19) * 100) / 100)}</b> (automatisch berechnet)
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Zurück</Button>
            <Button onClick={createPurchase} disabled={busy} data-testid="ankauf-create-button" className="bg-zinc-900 hover:bg-zinc-800 text-white">
              {busy ? "Erstelle…" : "Ankauf & Vertrag erstellen"} <Check className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {step === 3 && purchase && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="ankauf-sign-step">
          <h3 className="font-display font-medium text-lg">Vertrag {purchase.contract?.number} – digitale Unterschrift</h3>
          <p className="text-sm text-zinc-500">Der Verkäufer unterschreibt direkt hier (Smartphone, Tablet oder PC). Die Unterschrift wird dem Vertrag eindeutig zugeordnet.</p>
          <SignaturePad onConfirm={sign} confirmLabel="Vertrag unterschreiben & Ankauf abschließen" />
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadContract} className="flex-1 min-h-[44px]" data-testid="contract-download-button">
              <Download className="w-4 h-4 mr-1" /> PDF laden
            </Button>
            <Button variant="outline" onClick={shareContract} className="flex-1 min-h-[44px]" data-testid="contract-share-button">
              <Share2 className="w-4 h-4 mr-1" /> Teilen (E-Mail/WhatsApp)
            </Button>
          </div>
          <button onClick={() => navigate("/app/ankauf")} className="text-sm text-zinc-400 hover:text-zinc-900" data-testid="ankauf-skip-sign">Später unterschreiben →</button>
        </div>
      )}

      <BarcodeScan open={scanFor !== null} onClose={() => setScanFor(null)} title="Seriennummer / IMEI scannen"
        onResult={(code) => { if (scanFor !== null) setPos(scanFor, "serial", code); setScanFor(null); }} />

      <Dialog open={newSellerOpen} onOpenChange={setNewSellerOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="new-seller-dialog">
          <DialogHeader><DialogTitle>Neuen Verkäufer anlegen</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["first_name", "Vorname *"], ["last_name", "Nachname *"], ["birth_date", "Geburtsdatum"], ["phone", "Telefon"], ["email", "E-Mail"], ["address", "Adresse"], ["zip", "PLZ"], ["city", "Ort"], ["id_type", "Ausweisart (z. B. Personalausweis)"], ["id_number", "Ausweisnummer"]].map(([k, l]) => (
              <div key={k} className={k === "address" ? "col-span-2" : ""}>
                <Label>{l}</Label>
                <Input value={newSeller[k]} onChange={(e) => setNewSeller({ ...newSeller, [k]: e.target.value })} data-testid={`new-seller-${k}`} className="mt-1.5" />
              </div>
            ))}
          </div>
          <Button onClick={createSeller} disabled={!newSeller.first_name || !newSeller.last_name} data-testid="create-seller-button" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white mt-4">
            Verkäufer speichern & übernehmen
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
